import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ID } from '@clean-code/models/common';
import { ControlValueAccessorConnector } from '@clean-code/shared/components/ui-form-controls';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'client-autocomplete-dropdown',
  templateUrl: './client-autocomplete-dropdown.component.html',
  styleUrls: ['./client-autocomplete-dropdown.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClientAutocompleteDropdownComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    FontAwesomeModule,
    TranslocoModule,
  ],
})
export class ClientAutocompleteDropdownComponent
  extends ControlValueAccessorConnector
  implements AfterViewInit, OnDestroy
{
  faTimes = faTimes;
  searchFormControl = new FormControl(null);
  displayWith = (value: any) => (value && value[this.lableArgument]) ?? value;

  isLoading$ = new BehaviorSubject<boolean>(true);
  destroyed$ = new Subject<void>();

  @Input() placeholder: string;
  @Input() valueArgument: string;
  @Input() lableArgument: string;

  @Input({ required: true })
  public set isLoading(value: boolean) {
    this.isLoading$.next(value);
  }

  #searchTermValueSignal = signal('');

  data = input.required<any[]>();

  searchTermSignal = toSignal(
    this.searchFormControl.valueChanges.pipe(debounceTime(300)),
  );

  private get value() {
    if (!this.formControl) {
      return null;
    }
    return this.formControl?.value;
  }

  filteredData = computed(() => {
    const searchTerm = this.searchTermSignal();
    const data = this.data();
    if (!searchTerm || !data) {
      return data;
    }

    let lowerCaseSearchTerm = '';
    if (typeof searchTerm === 'object') {
      lowerCaseSearchTerm = searchTerm[this.lableArgument].toLowerCase();
    } else if (typeof searchTerm !== 'string') {
      lowerCaseSearchTerm = searchTerm.toString().toLowerCase();
    } else {
      lowerCaseSearchTerm = searchTerm.toLowerCase();
    }

    return data.filter((item: any) => {
      if (this.lableArgument) {
        return item[this.lableArgument]
          .toLowerCase()
          .includes(lowerCaseSearchTerm);
      } else {
        return (item as string).toLowerCase().includes(lowerCaseSearchTerm);
      }
    });
  });

  constructor() {
    super();
    effect(() => {
      this.searchFormControl.setValue(this.#searchTermValueSignal());
    });
    effect(
      () => {
        this.dataSignalEffect();
      },
      { allowSignalWrites: true },
    );
  }

  dataSignalEffect() {
    const data = this.data();

    if (!data || (data.length === 0 && this.value)) {
      return null;
    }

    const object = this.findValueFromData(this.value, data);

    if (object) {
      this.#searchTermValueSignal.set(object[this.lableArgument]);
    }

    this.isLoading$.next(false);
    return data;
  }

  ngAfterViewInit(): void {
    if (this.formControl.hasValidator(Validators.required)) {
      this.searchFormControl.setValidators([Validators.required]);
      this.searchFormControl.reset();
      this.searchFormControl.markAllAsTouched();
    }

    this.formControl.statusChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((status) => {
        if (status === 'DISABLED') {
          this.searchFormControl.disable();
        } else if (status === 'INVALID') {
          this.searchFormControl.markAllAsTouched();
        }
      });

    const value = this.formControl.value;
    this.formControl.reset();
    this.formControl.setValue(value);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private findValueFromData(id: ID, data: unknown[]): any {
    if ((!data || !id) && typeof data !== 'string') {
      return null;
    }

    this.isLoading$.next(false);

    return data.find((item: any) => +item[this.valueArgument] === +id);
  }

  public selectionChange(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value;
    if (this.valueArgument && typeof item === 'object') {
      this.formControl.setValue(item[this.valueArgument]);
      this.#searchTermValueSignal.set(item[this.lableArgument]);
    } else {
      this.formControl.setValue(item);
      this.#searchTermValueSignal.set(item);
    }
  }

  public clearSelection() {
    this.formControl.reset();
    this.#searchTermValueSignal.set('');
  }

  override writeValue(value: any): void {
    if (this.valueArgument && value && typeof value === 'object') {
      this.#searchTermValueSignal.set(value[this.lableArgument]);
    } else {
      const object = this.findValueFromData(value, this.data());

      if (object) {
        this.#searchTermValueSignal.set(object[this.lableArgument]);
      } else {
        this.#searchTermValueSignal.set(value);
      }
    }

    super.writeValue(value);
  }
}
